import { useLayout } from 'hooks/useLayout'
import { Modal } from 'components/Modal/Modal'
import { Box, Button } from '@mui/material'
import type { GridColDef } from '@mui/x-data-grid'
import { ModalFullBody } from 'components/Modal/ModalFullBody'
import { ModalFullFooter } from 'components/Modal/ModalFullFooter'
import { ModalFullHeader } from 'components/Modal/ModalFullHeader'
import { useGetPatronCheckInProgressForBadge } from 'hooks/api/Patron/useGetPatronCheckInProgressForBadge'
import { DataTable } from 'components/Shared/DataTable'
import { SectionHeader } from 'components/SectionHeader'
import { formatFullDateTime } from 'utils/util'
import GiftIcon from 'assets/gift_empty_state.svg'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'
import { type BadgeReward } from 'types/api'

const patronBadgeProgressColumns = (): GridColDef[] => {
  return [
    {
      field: 'licensedEstablishmentName',
      headerName: 'LE Name',
      valueGetter: (params) =>
        params.row.licensedEstablishment?.standardName
          ? params.row.licensedEstablishment?.standardName
          : params.row.licensedEstablishment?.name,
      flex: 1,
      minWidth: 160,
    },
    {
      field: 'licenseNumber',
      headerName: 'License Number',
      valueGetter: (params) => params.row.licensedEstablishment?.licenseNumber,
      flex: 1,
      minWidth: 160,
    },
    {
      field: 'city',
      headerName: 'City',
      valueGetter: (params) =>
        params.row.licensedEstablishment?.addresses[0]?.city,
      flex: 1,
      minWidth: 160,
    },
    {
      field: 'zip',
      headerName: 'Zip Code',
      valueGetter: (params) =>
        params.row.licensedEstablishment?.addresses[0]?.postalCode,
      flex: 1,
      minWidth: 160,
    },
    {
      field: 'createdOn',
      headerName: 'Date Visited',
      minWidth: 120,
      flex: 1.5,
      valueFormatter: (params: { value: string }) => {
        const date = formatFullDateTime(params.value)
        return date
      },
    },
  ]
}

export const PatronBadgeProgressModal = ({
  patronId,
  selectedBadgeReward,
  isModalOpen,
  toggleModalIsOpen,
}: {
  patronId: number
  selectedBadgeReward: BadgeReward | null
  isModalOpen: boolean
  toggleModalIsOpen: () => void
}) => {
  const { isMobile } = useLayout()
  const heightModal = isMobile ? '100%' : '80%'

  // query to get the patron's current check-in progress for the selected badge reward
  const patronCheckInProgressForBadgeQuery =
    useGetPatronCheckInProgressForBadge({
      patronId,
      badgeRewardId: selectedBadgeReward?.id ?? 0,
    })

  if (patronCheckInProgressForBadgeQuery.isPending) {
    return <ActivityIndicator />
  }

  const patronCheckInProgressForBadge = (
    patronCheckInProgressForBadgeQuery.data ?? []
  )
    .slice() // avoid changing the original array
    .sort(
      (a, b) =>
        new Date(a?.createdOn ?? 0).getTime() -
        new Date(b?.createdOn ?? 0).getTime()
    ) // sort by date
    .slice(0, selectedBadgeReward?.count) // only take the number of check-ins needed to earn the badge

  const onModalClose = () => {
    toggleModalIsOpen()
  }

  return (
    <Modal
      isOpen={isModalOpen}
      toggleIsOpen={() => {}}
      sx={{
        width: isMobile ? '100%' : '65%',
        height: heightModal,
      }}
    >
      <Box>
        <ModalFullHeader
          toggleIsOpen={toggleModalIsOpen}
          title={`${selectedBadgeReward?.name} Badge Details`}
        />
        <ModalFullBody heightModal={heightModal}>
          <form></form>
          <SectionHeader
            sx={{ paddingTop: 2 }}
            title="Patron Progress For This Badge"
            showButton={false}
          />
          <DataTable
            columns={patronBadgeProgressColumns()}
            rows={patronCheckInProgressForBadge ?? []}
            noDataMessage="Patron has no current progress for this badge."
            emptyDataImageSource={GiftIcon}
          />
        </ModalFullBody>
        <ModalFullFooter>
          <Button onClick={() => onModalClose()}>Close</Button>
        </ModalFullFooter>
      </Box>
    </Modal>
  )
}
